import React, { useState } from 'react';
import { Trash2, Move, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';

const FieldEditor = ({ field, onUpdate, onDelete }) => {
  const [expanded, setExpanded] = useState(false);

  // Handle field property changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    onUpdate({
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Add a new option to select, radio, or checkbox fields
  const addOption = () => {
    const options = [...field.options, `Option ${field.options.length + 1}`];
    onUpdate({ options });
  };

  // Update an option
  const updateOption = (index, value) => {
    const options = [...field.options];
    options[index] = value;
    onUpdate({ options });
  };

  // Remove an option
  const removeOption = (index) => {
    const options = field.options.filter((_, i) => i !== index);
    onUpdate({ options });
  };

  // Render field type specific options
  const renderFieldOptions = () => {
    if (!expanded) return null;

    switch (field.type) {
      case 'select':
      case 'radio':
      case 'checkbox':
        return (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            <div className="space-y-2">
              {field.options.map((option, index) => (
                <div key={index} className="flex items-center">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(index)}
                    className="ml-2 text-red-600 hover:text-red-800"
                    disabled={field.options.length <= 1}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addOption}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Option
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="border rounded-md overflow-hidden">
      <div className="p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Move className="h-5 w-5 text-gray-400 mr-2 cursor-grab" />
            <span className="font-medium text-gray-900">{field.label}</span>
            {field.required && (
              <span className="ml-1 text-red-500">*</span>
            )}
          </div>
          <div className="flex items-center">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="text-gray-500 hover:text-gray-700 mr-2"
            >
              {expanded ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-4">
            <div>
              <label htmlFor={`${field.id}-label`} className="block text-sm font-medium text-gray-700 mb-1">
                Field Label
              </label>
              <input
                type="text"
                id={`${field.id}-label`}
                name="label"
                value={field.label}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>

            {field.type !== 'radio' && field.type !== 'checkbox' && (
              <div>
                <label htmlFor={`${field.id}-placeholder`} className="block text-sm font-medium text-gray-700 mb-1">
                  Placeholder Text
                </label>
                <input
                  type="text"
                  id={`${field.id}-placeholder`}
                  name="placeholder"
                  value={field.placeholder}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
            )}

            <div className="flex items-center">
              <div 
                className={`relative flex items-center justify-center h-5 w-5 rounded border ${field.required 
                  ? 'bg-indigo-600 border-indigo-600' 
                  : 'bg-white border-gray-300'} 
                  transition-colors duration-200 ease-in-out cursor-pointer`}
                onClick={() => onUpdate({ required: !field.required })}
              >
                {field.required && (
                  <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <input
                  type="checkbox"
                  id={`${field.id}-required`}
                  name="required"
                  checked={field.required}
                  onChange={handleChange}
                  className="absolute opacity-0 h-0 w-0" // Hide the actual checkbox but keep it accessible
                />
              </div>
              <label 
                htmlFor={`${field.id}-required`} 
                className={`ml-3 block text-sm ${field.required ? 'font-medium text-gray-900' : 'text-gray-700'}`}
              >
                Required field
              </label>
            </div>

            {renderFieldOptions()}
          </div>
        )}
      </div>

      {/* Field Preview */}
      {!expanded && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
          <div className="text-xs text-gray-500">
            {getFieldTypeLabel(field.type)}
            {field.required && ' • Required'}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get human-readable field type label
const getFieldTypeLabel = (type) => {
  switch (type) {
    case 'text':
      return 'Text Field';
    case 'email':
      return 'Email Field';
    case 'tel':
      return 'Phone Number';
    case 'number':
      return 'Number Field';
    case 'textarea':
      return 'Text Area';
    case 'select':
      return 'Dropdown';
    case 'radio':
      return 'Radio Buttons';
    case 'checkbox':
      return 'Checkboxes';
    case 'date':
      return 'Date Field';
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

export default FieldEditor;
